/* React's view of the language: which one is active, and how to render an
   instant in it.
 *
 * Kept apart from `format.ts` so the formatters stay callable outside React —
 * CSV export needs them and is not a component. This file is the adapter,
 * `format.ts` is the logic. */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { createFormatter, type Formatter } from '@/shared/i18n/format';
import { FALLBACK_LOCALE, isLocale, type Locale } from '@/shared/i18n/locale';

/** The active language, narrowed to one the cockpit has a catalogue for.
 *
 * i18next reports `language` as a plain string and may report a resolved
 * fallback or a region-tagged variant. Narrowing here means no caller has to
 * think about it. */
export function useLocale(): Locale {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  return isLocale(language) ? language : FALLBACK_LOCALE;
}

/**
 * Date and number rendering bound to the active language.
 *
 * Rebuilt only when the language changes, so a table of two hundred rows
 * constructs its `Intl` formatters once rather than once per cell.
 */
export function useFormatter(): Formatter {
  const { t } = useTranslation();
  const locale = useLocale();
  return useMemo(() => createFormatter(locale, t), [locale, t]);
}
